import { supabase } from '../supabase/client';

export type BriefingPrio = 'dringend' | 'hoch' | 'mittel' | 'niedrig';

export interface BriefingMail {
  id: string;
  konto_id: string;
  von: string;
  an: string[];
  betreff: string;
  datum: string;
  text_inhalt: string | null;
  message_id: string | null;
  absender_name: string | null;
  objekt_name: string | null;
  prioritaet: BriefingPrio;
  ist_notfall: boolean;
  zusammenfassung: string | null;
  erkannte_aufgabe: string | null;
  naechster_schritt: string | null;
  frist_datum: string | null;
  zeitschaetzung_min: number | null;
  eff_zustaendig_id: string | null;
  eff_zustaendig_typ: string | null;
}

const PRIO_RANG: Record<BriefingPrio, number> = { dringend: 0, hoch: 1, mittel: 2, niedrig: 3 };

export function sortiereBriefingMails(mails: BriefingMail[]): BriefingMail[] {
  return [...mails].sort((a, b) => {
    const p = PRIO_RANG[a.prioritaet] - PRIO_RANG[b.prioritaet];
    if (p !== 0) return p;
    if (a.frist_datum !== b.frist_datum) {
      if (a.frist_datum === null) return 1;
      if (b.frist_datum === null) return -1;
      return a.frist_datum < b.frist_datum ? -1 : 1;
    }
    return a.datum < b.datum ? -1 : 1;
  });
}

/** Kritisch = Notfall ODER dringend ODER Frist heute/überfällig. */
export function istKritisch(m: BriefingMail, heuteIso: string): boolean {
  if (m.ist_notfall || m.prioritaet === 'dringend') return true;
  return m.frist_datum !== null && m.frist_datum <= heuteIso;
}

/** Offene eingehende Mails (nicht erledigt/archiviert, keine Werbung) mit KI-Analyse. */
export async function ladeBriefingMails(): Promise<BriefingMail[]> {
  const { data, error } = await supabase
    .from('emails')
    .select('id, konto_id, von, an, betreff, datum, text_inhalt, message_id, zustaendig_id, zustaendig_typ, ' +
      'mieter:mieter_id(vorname, nachname, firma_name), ' +
      'eigentuemer:eigentuemer_id(vorname, nachname, firma_name), ' +
      'objekt:objekt_id(bezeichnung, zustaendig_id, zustaendig_typ), ' +
      'analysen:email_analysen(prioritaet, ist_notfall, zusammenfassung, erkannte_aufgabe, naechster_schritt, frist_datum, zeitschaetzung_min)')
    .eq('richtung', 'eingang')
    .eq('erledigt', false)
    .eq('ist_werbung', false)
    .is('archiviert_am', null)
    .is('geloescht_am', null)
    .order('datum', { ascending: false })
    .limit(200);
  if (error) throw new Error(error.message);

  const mails: BriefingMail[] = [];
  for (const e of (data || []) as any[]) {
    const a = Array.isArray(e.analysen) ? e.analysen[0] : e.analysen;
    if (!a || !a.prioritaet) continue; // ohne Analyse nicht briefen
    const person = e.mieter || e.eigentuemer;
    mails.push({
      id: e.id, konto_id: e.konto_id, von: e.von, an: e.an || [], betreff: e.betreff || '(Ohne Betreff)',
      datum: e.datum, text_inhalt: e.text_inhalt ?? null, message_id: e.message_id ?? null,
      absender_name: person ? (person.firma_name || `${person.vorname || ''} ${person.nachname || ''}`.trim() || null) : null,
      objekt_name: e.objekt?.bezeichnung ?? null,
      prioritaet: a.prioritaet as BriefingPrio,
      ist_notfall: !!a.ist_notfall,
      zusammenfassung: a.zusammenfassung ?? null,
      erkannte_aufgabe: a.erkannte_aufgabe ?? null,
      naechster_schritt: a.naechster_schritt ?? null,
      frist_datum: a.frist_datum ?? null,
      zeitschaetzung_min: a.zeitschaetzung_min ?? null,
      eff_zustaendig_id: e.zustaendig_id ?? e.objekt?.zustaendig_id ?? null,
      eff_zustaendig_typ: (e.zustaendig_id ? e.zustaendig_typ : e.objekt?.zustaendig_typ) ?? null,
    });
  }
  return sortiereBriefingMails(mails);
}
